<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ConversationOperationsModal from './ConversationOperationsModal.vue';

const props = defineProps({
  chat: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const modalRef = ref(null);
const isClaiming = ref(false);
const showDropdown = ref(false);
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccount = computed(() => store.getters.getCurrentAccount);
const teamId = computed(() => props.chat.meta?.team?.id);
const isAssignee = computed(
  () => props.chat.meta?.assignee?.id === currentUser.value?.id
);
const isAdministrator = computed(
  () => currentAccount.value?.role === 'administrator'
);
const isSupervisor = computed(() =>
  (currentAccount.value?.supervised_team_ids || []).includes(teamId.value)
);

const canClaim = computed(
  () => props.chat.status === 'open' && !props.chat.meta?.assignee
);
const canResolve = computed(
  () =>
    ['open', 'pending'].includes(props.chat.status) &&
    (isAssignee.value || isAdministrator.value || isSupervisor.value)
);
const canReopen = computed(
  () =>
    props.chat.status === 'resolved' &&
    (isAdministrator.value || isSupervisor.value)
);
const canManage = computed(() => isAdministrator.value || isSupervisor.value);
const operationItems = computed(() => [
  {
    icon: 'i-lucide-arrow-right-left',
    label: t('CONVERSATION.OPERATIONS.TRANSFER.ACTION'),
    value: 'TRANSFER',
  },
  {
    icon: 'i-lucide-user-round-x',
    label: t('CONVERSATION.OPERATIONS.RELEASE.ACTION'),
    value: 'RELEASE',
  },
]);

const claim = async () => {
  isClaiming.value = true;
  try {
    await store.dispatch('claimConversation', props.chat.id);
    useAlert(t('CONVERSATION.OPERATIONS.CLAIM.SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.code === 'already_claimed'
        ? t('CONVERSATION.OPERATIONS.CLAIM.ALREADY_CLAIMED')
        : t('CONVERSATION.OPERATIONS.CLAIM.ERROR')
    );
  } finally {
    isClaiming.value = false;
  }
};

const openOperation = ({ value }) => {
  showDropdown.value = false;
  modalRef.value?.open(value);
};
</script>

<template>
  <div class="flex items-center gap-2">
    <Button
      v-if="canClaim"
      :label="t('CONVERSATION.OPERATIONS.CLAIM.ACTION')"
      icon="i-lucide-hand"
      size="sm"
      :is-loading="isClaiming"
      @click="claim"
    />
    <Button
      v-if="canResolve"
      :label="t('CONVERSATION.OPERATIONS.RESOLVE.ACTION')"
      icon="i-lucide-circle-check"
      size="sm"
      color="slate"
      @click="modalRef?.open('RESOLVE')"
    />
    <Button
      v-if="canReopen"
      :label="t('CONVERSATION.OPERATIONS.REOPEN.ACTION')"
      icon="i-lucide-rotate-ccw"
      size="sm"
      color="slate"
      @click="modalRef?.open('REOPEN')"
    />
    <div
      v-if="canManage"
      v-on-clickaway="() => (showDropdown = false)"
      class="relative"
    >
      <Button
        :label="t('CONVERSATION.OPERATIONS.MANAGE')"
        icon="i-lucide-chevron-down"
        size="sm"
        variant="ghost"
        color="slate"
        @click="showDropdown = !showDropdown"
      />
      <DropdownMenu
        v-if="showDropdown"
        :menu-items="operationItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="openOperation"
      />
    </div>
    <ConversationOperationsModal ref="modalRef" :chat="chat" />
  </div>
</template>
